import { Close } from '@firmable/icons';
import * as ToastPrimitives from '@radix-ui/react-toast';
import React from 'react';
import { cn } from '../shadcn/cn';
import { Toast, ToastProvider, ToastTitle, ToastViewport } from '../shadcn/toast';

export interface ToasterItem {
  id: string;
  message: React.ReactNode;
  /** Shows the ✕. Default true. Leave it off for a glanceable confirmation ("Copied"); keep it for anything the user has to read. */
  closeButton?: boolean;
}

interface ToasterProps {
  /** The toasts to show, newest last. */
  toasts: ToasterItem[];
  /** Called once a toast has closed (timer, swipe or the close button) and its exit has played. */
  onDismiss: (id: string) => void;
  /** Spoken name of the close button. */
  closeLabel: string;
  /** How long each toast stays, in ms. */
  duration?: number;
  /** `page`: top centre of the window. `panel`: full width at the top of the nearest positioned container, under a 48px header (the extension panel). */
  placement?: 'page' | 'panel';
}

// Matches the toast exit animation, so the item leaves the list only after it has slid away.
const EXIT_MS = 150;

function ToasterToast({
  id,
  message,
  closeButton = true,
  onDismiss,
  closeLabel,
  duration,
}: ToasterItem & Pick<ToasterProps, 'onDismiss' | 'closeLabel' | 'duration'>) {
  const [open, setOpen] = React.useState(true);

  const onOpenChange = React.useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setOpen(false);
        setTimeout(() => onDismiss(id), EXIT_MS);
      }
    },
    [onDismiss, id],
  );

  const handleClose = React.useCallback(() => onOpenChange(false), [onOpenChange]);

  return (
    <Toast
      duration={duration}
      open={open}
      onOpenChange={onOpenChange}
      className={cn(
        'flex items-center justify-between space-x-0 border-0 bg-surface-darker text-inherit rounded-radius-md shadow-overlay p-lg transition-none',
        'data-[state=open]:animate-toastSlideDown data-[state=closed]:animate-toastSlideUp',
        'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-[transform_200ms_ease-out] data-[swipe=end]:animate-swipeOut motion-reduce:animate-none',
      )}>
      <ToastTitle className="overflow-hidden break-words text-body-large font-normal text-white">{message}</ToastTitle>
      {closeButton && (
        <ToastPrimitives.Action className="cursor-pointer text-white" altText={closeLabel} onClick={handleClose}>
          <Close />
        </ToastPrimitives.Action>
      )}
    </Toast>
  );
}

// The app's toast stack: dark toasts, each closing itself after `duration`.
// Built on the shadcn Toast (Radix Toast underneath).
function Toaster({ toasts, onDismiss, closeLabel, duration = 3000, placement = 'page' }: ToasterProps) {
  return (
    <ToastProvider>
      {toasts.map((toast) => (
        <ToasterToast key={toast.id} {...toast} onDismiss={onDismiss} closeLabel={closeLabel} duration={duration} />
      ))}
      <ToastViewport
        className={cn(
          'flex flex-col gap-sm m-0 max-h-none list-none z-[2147483647] rounded-radius-md sm:bottom-auto focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stroke-interactive',
          {
            '[--viewport-padding:_25px] fixed top-0 left-1/2 -translate-x-1/2 p-[var(--viewport-padding)] w-[390px] max-w-[100vw] sm:top-0 sm:right-auto md:max-w-[100vw] focus-visible:ring-inset':
              placement === 'page',
            'absolute top-12 inset-x-md p-0 w-auto max-w-none sm:top-12 sm:right-md md:max-w-none':
              placement === 'panel',
          },
        )}
      />
    </ToastProvider>
  );
}

export { Toaster };
